import type { Session } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { PREVIEW, PREVIEW_SIGNED_OUT } from "./preview";
import { supabase } from "./supabase";

/** Minimal view of the signed-in user the shell needs. */
export interface SignedInUser {
  id: string;
  email: string;
}

/**
 * Current Supabase session: `undefined` while loading, `null` when signed out.
 * In DEV preview mode a stand-in session is returned without touching Supabase.
 */
export function useSession(): SignedInUser | null | undefined {
  const [user, setUser] = useState<SignedInUser | null | undefined>(undefined);

  useEffect(() => {
    if (PREVIEW) {
      if (PREVIEW_SIGNED_OUT) setUser(null);
      else void import("../dev/fixtures").then(({ profile }) => setUser({ id: profile.id, email: profile.email ?? "" }));
      return;
    }
    const toUser = (s: Session | null) => (s ? { id: s.user.id, email: s.user.email ?? "" } : null);
    supabase.auth.getSession().then(({ data }) => setUser(toUser(data.session)));
    const { data } = supabase.auth.onAuthStateChange((_e, s) => setUser(toUser(s)));
    return () => data.subscription.unsubscribe();
  }, []);

  return user;
}

/** Magic link sign-in. Accounts are invited by a director; sign-up is disabled. */
export async function sendMagicLink(email: string): Promise<{ error: string | null }> {
  if (PREVIEW) {
    await new Promise((r) => setTimeout(r, 600));
    return { error: email.startsWith("error") ? "Signups not allowed for otp" : null };
  }
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: false, emailRedirectTo: window.location.origin },
  });
  return { error: error ? error.message : null };
}

export async function signOut(): Promise<void> {
  if (PREVIEW) {
    window.location.search = "?preview=login";
    return;
  }
  await supabase.auth.signOut();
}
